"use client";

import {useEffect, useState, useCallback} from "react";
import {KakaoLoader} from "@utils";

const useKakaoMap = ({mapId}: {mapId: string; defaultLevel?: number}) => {
  const [map, setMap] = useState<kakao.maps.Map | null>(null);
  const [markers, setMarkers] = useState<kakao.maps.Marker[]>([]);

  async function drawMap(attempt: number = 0) {
    try {
      const kakao = await KakaoLoader();
      const container = document.getElementById(mapId);
      if (!container?.clientHeight) {
        if (attempt < 5) {
          setTimeout(() => {
            drawMap(attempt + 1);
          }, 200);
        }
        return;
      }

      const mapInstance = new kakao.maps.Map(container, {
        center: new kakao.maps.LatLng(37.5665, 126.978),
        level: 3,
      });
      setMap(mapInstance);
    } catch (error) {
      console.error("Kakao Map 로드 실패:", error);
      document.getElementById(mapId)?.remove();
    }
  }

  useEffect(() => {
    drawMap();
  }, [mapId]);

  /** 위도/경도로 중심 이동 & 마커 표시 */
  const setCenterByLatLng = useCallback(
    (lat: number, lng: number) => {
      if (!map) return;

      const position = new window.kakao.maps.LatLng(lat, lng);
      map.setCenter(position);

      const marker = new window.kakao.maps.Marker({
        map,
        position,
      });

      setMarkers(prev => [...prev, marker]);
    },
    [map],
  );

  /** 주소로 중심 이동 & 마커 표시 */
  const setCenterByAddress = useCallback(
    (address: string): Promise<{lat: number; lng: number} | null> => {
      if (!map) return Promise.resolve(null);

      return new Promise((resolve, reject) => {
        const geocoder = new window.kakao.maps.services.Geocoder();
        geocoder.addressSearch(address, (result, status) => {
          if (status === window.kakao.maps.services.Status.OK) {
            const coords = new window.kakao.maps.LatLng(
              Number(result[0].y),
              Number(result[0].x),
            );
            map.setCenter(coords);

            const marker = new window.kakao.maps.Marker({
              map,
              position: coords,
            });

            // 여러 개 마커 관리
            setMarkers(prev => [...prev, marker]);

            resolve({lat: coords.getLat(), lng: coords.getLng()});
          } else {
            reject(new Error("주소 검색 실패"));
          }
        });
      });
    },
    [map],
  );

  /** 현재 찍힌 마커들의 좌표 가져오기 */
  const getMarkerPositions = useCallback(() => {
    return markers.map(marker => {
      const pos = marker.getPosition();
      return {lat: pos.getLat(), lng: pos.getLng()};
    });
  }, [markers]);

  return {
    map,
    setCenterByLatLng,
    setCenterByAddress,
    getMarkerPositions,
    markers,
    drawMap,
  };
};

export default useKakaoMap;
